import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { CallMade, CallReceived, CheckCircleOutlined, ContentCopy, OpenInNew, Payments, PersonOutline, ReceiptLong, Refresh, RemoveCircleOutline, Replay, SyncProblem } from '@mui/icons-material';

import { Button, CenterProgressSpinner, Chip, ChipsNavigation, HStack, IconButton, Table } from '@dw/components/core';
import { DATE_FORMATS, ROUTES } from '@dw/constants';
import { useAuth, useConfirm, useSnackbar } from '@dw/hooks';
import { useDismissAllSyncIssuesMutation, useDismissSyncIssueMutation, useGetSyncIssues, useRetryAllSyncIssuesMutation, useRetrySyncIssueMutation } from '@dw/hooks/api/SyncIssueApi';
import { USER_ROLE } from '@dw/models/danceSchoolUser';
import { IMenuItem, TColor } from '@dw/models/shared';
import { ISyncIssue, SYNC_FAILURE_CATEGORY, SYNC_DIRECTION, SYNC_ENTITY_TYPE, SYNC_ISSUE_STATUS, SYNC_PROVIDER } from '@dw/models/syncIssue';
import { formatRoute, splitByUpperCase } from '@dw/utils';

type TSyncIssueFilter = 'all' | SYNC_ISSUE_STATUS.FAILED | SYNC_ISSUE_STATUS.RETRYING;

const FILTER_OPTIONS: IMenuItem[] = [
  { text: 'Failed', value: SYNC_ISSUE_STATUS.FAILED },
  { text: 'Retrying', value: SYNC_ISSUE_STATUS.RETRYING },
  { text: 'All', value: 'all' }
];

const getCategoryColor = (category: SYNC_FAILURE_CATEGORY): TColor | undefined => {
  switch (category) {
    case SYNC_FAILURE_CATEGORY.INVOICE_LOCKED:
    case SYNC_FAILURE_CATEGORY.VALIDATION:
      return 'warning';
    case SYNC_FAILURE_CATEGORY.RATE_LIMITED:
    case SYNC_FAILURE_CATEGORY.TRANSIENT:
      return 'info';
    case SYNC_FAILURE_CATEGORY.AUTH_EXPIRED:
    case SYNC_FAILURE_CATEGORY.NOT_FOUND:
      return 'error';
    default:
      return undefined;
  }
};

const getEntityIcon = (entityType: SYNC_ENTITY_TYPE) => {
  switch (entityType) {
    case SYNC_ENTITY_TYPE.CONTACT: return <PersonOutline fontSize='small' color='action' />;
    case SYNC_ENTITY_TYPE.PAYMENT: return <Payments fontSize='small' color='action' />;
    default: return <ReceiptLong fontSize='small' color='action' />;
  }
};

interface ISyncIssuesReportProps {
  provider?: SYNC_PROVIDER;
}

export const SyncIssuesReport: FunctionComponent<ISyncIssuesReportProps> = ({ provider = SYNC_PROVIDER.XERO }) => {

  const { userHasRole } = useAuth();
  const { confirm } = useConfirm();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const canManage = userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN);

  const [statusFilter, setStatusFilter] = useState<TSyncIssueFilter>(SYNC_ISSUE_STATUS.FAILED);

  const { data: syncIssues, isLoading, isRefetching, refetch } = useGetSyncIssues(provider, statusFilter, { enabled: canManage });

  const { mutate: retrySyncIssue, isPending: retryLoading, variables: retryVariables } = useRetrySyncIssueMutation();
  const { mutate: dismissSyncIssue, isPending: dismissLoading, variables: dismissVariables } = useDismissSyncIssueMutation();
  const { mutate: retryAllSyncIssues, isPending: retryAllLoading } = useRetryAllSyncIssuesMutation();
  const { mutate: dismissAllSyncIssues, isPending: dismissAllLoading } = useDismissAllSyncIssuesMutation();

  const issues = syncIssues ?? [];
  const actionsDisabled = retryLoading || dismissLoading || retryAllLoading || dismissAllLoading;
  const hasFailedIssues = statusFilter !== SYNC_ISSUE_STATUS.RETRYING && issues.some((issue) => issue.status === SYNC_ISSUE_STATUS.FAILED);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id).then(() => showSnackbar('Sync issue ID copied to clipboard'));
  };

  const handleDismiss = (issue: ISyncIssue) => {
    confirm({
      title: 'Dismiss sync issue?',
      message: 'Dismissing hides this issue from the list. Use it once the record has been fixed in Xero directly, or no longer needs to sync.',
      positiveText: 'Dismiss',
      onPositive: () => dismissSyncIssue(issue.id)
    });
  };

  const handleRetryAll = () => {
    confirm({
      title: 'Retry all failed syncs?',
      message: "Every failed item that can be retried will be re-queued. Items that can't be retried are skipped \u2014 fix those in Xero, then dismiss them.\n\nThis applies to every failed sync, not just the ones shown by the current filter.",
      positiveText: 'Retry all',
      onPositive: () => retryAllSyncIssues(provider)
    });
  };

  const handleDismissAll = () => {
    confirm({
      title: 'Dismiss all failed syncs?',
      message: 'Dismissing hides every failed issue from the list. Use it once the records have been fixed in Xero directly, or no longer need to sync.\n\nThis applies to every failed sync, not just the ones shown by the current filter.',
      positiveText: 'Dismiss all',
      onPositive: () => dismissAllSyncIssues(provider)
    });
  };

  const renderEntityLink = (row: ISyncIssue) => {
    if (row.entityType === SYNC_ENTITY_TYPE.CONTACT) {
      return (
        <IconButton
          icon={<OpenInNew fontSize='small' />}
          size='small'
          tooltip='View account'
          onClick={() => navigate(formatRoute(ROUTES.ViewDancerAccount, { accountId: row.entityId }))}
        />
      );
    }

    return (
      <IconButton
        icon={<OpenInNew fontSize='small' />}
        size='small'
        tooltip='View invoice'
        href={formatRoute(ROUTES.PublicInvoiceView, { invoiceId: row.entityId })}
        target='_blank'
      />
    );
  };

  if (!canManage) {
    return null;
  }

  return (
    <Stack rowGap={3}>
      <HStack justifyContent='space-between' alignItems='flex-start'>
        <HStack breakpoint={false} columnGap={1.5} alignItems='flex-start'>
          {issues.length > 0 ? <SyncProblem color='warning' /> : <CheckCircleOutlined color='success' />}
          <Stack>
            <Typography fontWeight={500}>Xero Sync Health</Typography>
            <Typography variant='caption'>
              Invoices, contacts and payments that failed to sync with Xero. Retry once the cause is cleared, or dismiss to hide.
            </Typography>
          </Stack>
        </HStack>

        <HStack breakpoint={false} columnGap={1}>
          {hasFailedIssues && (
            <>
              <Button
                size='small'
                variant='outlined'
                color='primary'
                startIcon={<Replay />}
                loading={retryAllLoading}
                disabled={actionsDisabled}
                onClick={handleRetryAll}
              >
                Retry all
              </Button>
              <Button
                size='small'
                variant='outlined'
                color='error'
                startIcon={<RemoveCircleOutline />}
                loading={dismissAllLoading}
                disabled={actionsDisabled}
                onClick={handleDismissAll}
              >
                Dismiss all
              </Button>
              <Divider orientation='vertical' flexItem />
            </>
          )}
          <ChipsNavigation
            activeItem={statusFilter}
            optionSelected={(option) => setStatusFilter(option.value as TSyncIssueFilter)}
            options={FILTER_OPTIONS}
          />
          <IconButton
            icon={<Refresh />}
            tooltip='Refresh'
            loading={isRefetching}
            onClick={() => refetch()}
          />
        </HStack>
      </HStack>

      {isLoading ? (
        <CenterProgressSpinner />
      ) : issues.length < 1 ? (
        <Alert severity='success' icon={<CheckCircleOutlined />}>
          No sync issues — Xero is up to date.
        </Alert>
      ) : (
        <Table
          addPagination
          keyAttribute='id'
          data={issues}
          columns={[
            {
              label: 'Type',
              element: (row) => (
                <Stack rowGap={0.25}>
                  <HStack breakpoint={false} columnGap={1}>
                    {getEntityIcon(row.entityType)}
                    <Typography variant='body2'>{splitByUpperCase(row.entityType)}</Typography>
                    {renderEntityLink(row)}
                  </HStack>
                  <HStack breakpoint={false} columnGap={0.25} alignItems='center'>
                    <Tooltip title={`Sync issue ID: ${row.id}`}>
                      <Typography variant='caption' sx={{ fontFamily: 'monospace' }}>{row.id.slice(0, 8)}</Typography>
                    </Tooltip>
                    <IconButton
                      icon={<ContentCopy fontSize='small' />}
                      size='small'
                      tooltip='Copy ID'
                      onClick={() => handleCopyId(row.id)}
                    />
                  </HStack>
                </Stack>
              )
            },
            {
              label: 'Direction',
              element: (row) => {
                const isOutbound = row.direction === SYNC_DIRECTION.OUTBOUND;
                return (
                  <Tooltip title={isOutbound ? 'Dance World → Xero' : 'Xero → Dance World'}>
                    <span>
                      <HStack breakpoint={false} columnGap={1}>
                        {isOutbound ? <CallMade fontSize='small' color='action' /> : <CallReceived fontSize='small' color='action' />}
                        <Typography variant='body2'>{splitByUpperCase(row.direction)}</Typography>
                      </HStack>
                    </span>
                  </Tooltip>
                );
              }
            },
            {
              label: 'Reason',
              element: (row) => (
                <Tooltip title={row.failureReason}>
                  <Typography variant='body2'>{row.failureReason}</Typography>
                </Tooltip>
              )
            },
            {
              label: 'Category',
              element: (row) => (
                <Chip
                  size='small'
                  color={getCategoryColor(row.failureCategory)}
                  label={splitByUpperCase(row.failureCategory)}
                />
              )
            },
            {
              label: 'Attempts',
              element: (row) => <Typography variant='body2'>{row.attemptCount}</Typography>
            },
            {
              label: 'Last Failed',
              element: (row) => <Typography variant='body2'>{row.lastFailedUtc.format(DATE_FORMATS.FullDateTime)}</Typography>
            },
            {
              label: 'Actions',
              disableRowClick: true,
              element: (row) => {
                const isRetrying = row.status === SYNC_ISSUE_STATUS.RETRYING;

                if (row.status !== SYNC_ISSUE_STATUS.FAILED && !isRetrying) {
                  return (
                    <Chip
                      size='small'
                      color={row.status === SYNC_ISSUE_STATUS.RESOLVED ? 'success' : undefined}
                      label={splitByUpperCase(row.status)}
                    />
                  );
                }

                return (
                  <HStack breakpoint={false} columnGap={0.5}>
                    {isRetrying && (
                      <Tooltip title={row.lastRetriedUtc ? `Retry queued ${row.lastRetriedUtc.format(DATE_FORMATS.FullDateTime)}` : ''}>
                        <Chip size='small' color='info' label={splitByUpperCase(row.status)} />
                      </Tooltip>
                    )}
                    <IconButton
                      icon={<Replay />}
                      tooltip={row.canRetry ? 'Retry sync' : "This issue cannot be retried — fix it in Xero, then dismiss"}
                      color='primary'
                      disabled={!row.canRetry || actionsDisabled}
                      loading={retryLoading && retryVariables === row.id}
                      onClick={() => retrySyncIssue(row.id)}
                    />
                    <IconButton
                      icon={<RemoveCircleOutline />}
                      tooltip='Dismiss'
                      color='error'
                      disabled={actionsDisabled}
                      loading={dismissLoading && dismissVariables === row.id}
                      onClick={() => handleDismiss(row)}
                    />
                  </HStack>
                );
              }
            }
          ]}
        />
      )}
    </Stack>
  );
};
